import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Breadcrumbs,
  Grid,
} from "@mui/material";
import {
  SportsEsportsOutlined as MineIcon,
  NavigateNext as NavigateNextIcon,
  AutoAwesome as SparkleIcon,
  Bolt as InstantIcon,
  ShieldOutlined as FairIcon,
  EmojiEventsOutlined as PrizeIcon,
  LocalFireDepartment as HotIcon,
  ConfirmationNumberOutlined as TicketIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { Path, DEFAULT_CURRENCY } from "../../../../../static/Constants";
import { CommonChip } from "../../../../Components/ui";

const MineHeroBanner = ({
  totalActive = 0,
  combinedPrize = "0.00",
  lowestEntry = "0.00",
  currency = DEFAULT_CURRENCY,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumbs Navigation */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              sx={{ fontSize: 16, color: colors.slate[400] }}
            />
          }
          aria-label="breadcrumb"
          sx={{ fontSize: "0.875rem" }}
        >
          <Link
            to={Path.root}
            style={{
              textDecoration: "none",
              color: colors.slate[500],
              fontWeight: 500,
            }}
          >
            Home
          </Link>
          <Link
            to={Path.allLotteries}
            style={{
              textDecoration: "none",
              color: colors.slate[500],
              fontWeight: 500,
            }}
          >
            All Lotteries
          </Link>
          <Typography
            sx={{
              color: colors.purple[600],
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            MineBET Arena
          </Typography>
        </Breadcrumbs>
      </Stack>

      {/* Hero Banner Box */}
      <Box className="mine-hero-banner" sx={{ p: { xs: 3, md: 4 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: colors.purple[50],
                  color: colors.purple[600],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(124, 58, 237, 0.15)",
                }}
              >
                <MineIcon sx={{ fontSize: 26 }} />
              </Box>
              <div>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.purple[600],
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                >
                  TACTICAL LIVE ARENA
                </Typography>
                <Typography variant="h4" className="mine-hero-title">
                  Autobet Mine
                </Typography>
              </div>
            </Stack>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                maxWidth: 540,
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              Step onto the decentralized minefield matrix. Pick your 1 lucky tile from open game boards and compete for transparent on-chain prize pools with verifiable VRF outcomes!
            </Typography>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
              <CommonChip
                icon={<InstantIcon sx={{ fontSize: 16 }} />}
                label="Pick 1 Lucky Number"
                variant="neutral"
                size="small"
              />
              <CommonChip
                icon={<FairIcon sx={{ fontSize: 15 }} />}
                label="Provably Fair VRF"
                variant="neutral"
                size="small"
              />
              <CommonChip
                icon={<SparkleIcon sx={{ fontSize: 15 }} />}
                label="Instant Smart Contract Payouts"
                variant="neutral"
                size="small"
              />
            </Stack>
          </Grid>

          {/* Quick Metrics Cards */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box className="mine-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <HotIcon sx={{ fontSize: 18, color: colors.purple[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Active Minefields
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.slate[900] }}
                  >
                    {totalActive}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.emerald[600], fontWeight: 600 }}
                  >
                    ● Live on-chain
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="mine-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <PrizeIcon sx={{ fontSize: 18, color: colors.amber[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Total Prize Pool
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.purple[700] }}
                  >
                    {combinedPrize} {currency}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Across all games
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="mine-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <TicketIcon sx={{ fontSize: 18, color: colors.blue[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Entry From
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: colors.slate[900] }}
                  >
                    {lowestEntry} {currency}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Per single ticket
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="mine-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <FairIcon sx={{ fontSize: 18, color: colors.emerald[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Decentralized
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: colors.slate[900] }}
                  >
                    100% On-Chain
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Trustless execution
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

MineHeroBanner.propTypes = {
  totalActive: PropTypes.number,
  combinedPrize: PropTypes.string,
  lowestEntry: PropTypes.string,
  currency: PropTypes.string,
};

export default MineHeroBanner;
